import { Injectable, signal, WritableSignal } from '@angular/core';
import { AppState, UiFilterState } from '../models/app-state.model';
import { Category } from '../models/category.model';
import { Card } from '../models/card.model';
import { StorageService } from './storage.service';
import { ImportExportService } from './import-export.service';
import { validateAppState } from '../validators/app-state.validator';
import {
  ValidationResult,
  validateCategoryCreate,
  validateCategoryDelete,
  validateCategoryEdit,
} from '../validators';
import { validateParentAssignment } from '../validators/parent-child.validator';
import { createDefaultAppState, createDefaultCard, createDefaultUiFilter, generateId } from '../utils/factories';
import {
  createSequentialLadderLevels,
  moveHierarchyEntry,
  normalizeHierarchicalLadder,
  normalizeLane,
  reorderChildWithinHierarchy,
} from '../utils/ladder.helper';

export interface CardDraft {
  title: string;
  description: string;
  notes: string;
  categoryId: string;
  status: Card['status'];
  deadline: string | null;
  showInLadder: boolean;
  parentCardId: string | null;
  manualProgress: number;
  ladderGroupColor?: string | null;
}

export interface CardOperationResult extends ValidationResult {
  cardId?: string;
}

type UiFilterView = Exclude<keyof AppState['ui'], 'legacy'>;

@Injectable({
  providedIn: 'root',
})
export class AppStateService {
  public state: WritableSignal<AppState>;

  constructor(
    private storage: StorageService,
    private importExportService: ImportExportService = new ImportExportService(),
  ) {
    this.state = signal(createDefaultAppState());
  }

  public mutateState(recipe: (draft: AppState) => void): boolean {
    const currentState = this.state();

    const draftState: AppState = structuredClone(currentState);

    recipe(draftState);

    draftState.savedAt = new Date().toISOString();

    return this.applyAndPersistState(draftState);
  }

  public overrideState(newState: AppState): boolean {
    const stateToApply: AppState = structuredClone(newState);
    stateToApply.savedAt = new Date().toISOString();

    return this.applyAndPersistState(stateToApply);
  }

  public createExportSnapshot() {
    const snapshotState = structuredClone(this.state());
    snapshotState.savedAt = new Date().toISOString();

    return this.importExportService.createExportSnapshot(snapshotState);
  }

  public updateUiFilter(view: UiFilterView, patch: Partial<UiFilterState>): boolean {
    return this.mutateState((draft) => {
      draft.ui[view] = {
        ...draft.ui[view],
        ...patch,
      };
    });
  }

  public resetUiFilter(view: UiFilterView): boolean {
    return this.mutateState((draft) => {
      draft.ui[view] = createDefaultUiFilter();
    });
  }

  public createCategory(name: string, color: string): ValidationResult {
    const nextCategory: Category = {
      id: generateId('cat'),
      name: name.trim(),
      color,
      isSystem: false,
    };
    const validationResult = validateCategoryCreate(nextCategory, this.state().categories);

    if (!validationResult.isValid) {
      return validationResult;
    }

    return this.commitCategoryMutation((draft) => {
      draft.categories.push(nextCategory);
    }, 'Failed to create category.');
  }

  public updateCategory(categoryId: string, name: string, color: string): ValidationResult {
    const originalCategory = this.state().categories.find(
      (category: Category) => category.id === categoryId,
    );

    if (!originalCategory) {
      return this.createErrorResult('Category not found.');
    }

    const updatedCategory: Category = {
      ...originalCategory,
      name: name.trim(),
      color,
    };
    const validationResult = validateCategoryEdit(
      updatedCategory,
      originalCategory,
      this.state().categories,
    );

    if (!validationResult.isValid) {
      return validationResult;
    }

    return this.commitCategoryMutation((draft) => {
      draft.categories = draft.categories.map((category) =>
        category.id === categoryId ? updatedCategory : category,
      );
    }, 'Failed to update category.');
  }

  public deleteCategory(
    categoryId: string,
    replacementCategoryId: string | null = null,
  ): ValidationResult {
    const currentState = this.state();
    const category = currentState.categories.find(
      (candidate: Category) => candidate.id === categoryId,
    );

    if (!category) {
      return this.createErrorResult('Category not found.');
    }

    const cardsUsingCategory = currentState.cards.filter(
      (card: Card) => card.categoryId === categoryId,
    );
    const validationResult = validateCategoryDelete(
      categoryId,
      cardsUsingCategory.length,
      replacementCategoryId,
    );

    if (!validationResult.isValid) {
      return validationResult;
    }

    if (
      replacementCategoryId !== null &&
      !currentState.categories.some((candidate: Category) => candidate.id === replacementCategoryId)
    ) {
      return this.createErrorResult('Replacement category does not exist.');
    }

    return this.commitCategoryMutation((draft) => {
      if (replacementCategoryId !== null) {
        draft.cards = draft.cards.map((card) =>
          card.categoryId === categoryId
            ? { ...card, categoryId: replacementCategoryId, updatedAt: new Date().toISOString() }
            : card,
        );
      }

      draft.categories = draft.categories.filter((candidate) => candidate.id !== categoryId);
    }, 'Failed to delete category.');
  }

  public createCard(draft: CardDraft): CardOperationResult {
    const now = new Date().toISOString();
    const nextCard = this.mergeCardDraftIntoCard(
      {
        ...createDefaultCard(),
        createdAt: now,
        updatedAt: now,
      },
      draft,
      now,
    );

    const parentValidation = this.validateParentChange(
      nextCard,
      draft.parentCardId,
      this.state().cards,
    );
    if (!parentValidation.isValid) {
      return parentValidation;
    }

    return this.commitCardMutation(
      (draftState) => {
        this.applyLadderRulesForNewCard(draftState, nextCard);
        draftState.cards.push(nextCard);
        this.normalizeLadderHierarchyInState(draftState);
      },
      'Failed to create card.',
      nextCard.id,
    );
  }

  public updateCard(cardId: string, draft: CardDraft): CardOperationResult {
    const currentCard = this.state().cards.find((card: Card) => card.id === cardId);

    if (!currentCard) {
      return this.createErrorResult('Card not found.');
    }

    const updatedCard = this.mergeCardDraftIntoCard(
      currentCard,
      draft,
      new Date().toISOString(),
    );

    if (!draft.showInLadder) {
      updatedCard.priority = null;
      updatedCard.importance = null;
    }

    const parentValidation = this.validateParentChange(
      updatedCard,
      draft.parentCardId,
      this.state().cards,
    );
    if (!parentValidation.isValid) {
      return parentValidation;
    }

    return this.commitCardMutation((draftState) => {
      const previousCard = draftState.cards.find((card: Card) => card.id === cardId);
      const previousPriority = previousCard?.priority ?? null;
      const wasInLadder = previousCard?.showInLadder ?? false;
      const previousParentId = previousCard?.parentCardId ?? null;

      draftState.cards = draftState.cards.map((card) => (card.id === cardId ? updatedCard : card));
      this.applyLadderRulesForExistingCard(
        draftState,
        updatedCard,
        wasInLadder,
        previousPriority,
        previousParentId,
      );
      this.normalizeLadderHierarchyInState(draftState);
    }, 'Failed to update card.');
  }

  public deleteCard(cardId: string): CardOperationResult {
    const currentState = this.state();
    const card = currentState.cards.find((candidate: Card) => candidate.id === cardId);

    if (!card) {
      return this.createErrorResult('Card not found.');
    }

    const childCount = currentState.cards.filter(
      (candidate: Card) => candidate.parentCardId === cardId,
    ).length;
    if (childCount > 0) {
      return this.createErrorResult(
        'This card cannot be deleted because it still has child cards.',
      );
    }

    return this.commitCardMutation((draft) => {
      draft.cards = draft.cards.filter((candidate) => candidate.id !== cardId);

      if (card.showInLadder && card.priority !== null) {
        const remainingLaneCards = draft.cards.filter(
          (candidate: Card) => candidate.showInLadder && candidate.priority === card.priority,
        );
        const normalized = normalizeLane(remainingLaneCards);
        draft.cards = draft.cards.map(
          (candidate) => normalized.find((item) => item.id === candidate.id) ?? candidate,
        );
      }
    }, 'Failed to delete card.');
  }

  public adjustManualProgress(cardId: string, delta: number): CardOperationResult {
    const card = this.state().cards.find((candidate: Card) => candidate.id === cardId);
    if (!card) {
      return this.createErrorResult('Card not found.');
    }

    const childCount = this.state().cards.filter(
      (candidate: Card) => candidate.parentCardId === cardId,
    ).length;
    if (childCount > 0) {
      return this.createErrorResult('Progress can only be edited on leaf cards.');
    }

    const nextProgress = Math.min(100, Math.max(0, card.manualProgress + delta));

    return this.commitCardMutation((draft) => {
      draft.cards = draft.cards.map((candidate) =>
        candidate.id === cardId
          ? { ...candidate, manualProgress: nextProgress, updatedAt: new Date().toISOString() }
          : candidate,
      );
    }, 'Failed to update progress.');
  }

  public addLadderLevel(): ValidationResult {
    const didCommit = this.mutateState((draft) => {
      const nextLevel = draft.ladder.levels.length === 0 ? 1 : Math.max(...draft.ladder.levels) + 1;
      draft.ladder.levels = [...draft.ladder.levels, nextLevel];
    });

    return didCommit ? { isValid: true, errors: [] } : this.createErrorResult('Failed to add ladder level.');
  }

  public deleteLadderLevel(level: number): ValidationResult {
    const currentState = this.state();

    if (!currentState.ladder.levels.includes(level)) {
      return this.createErrorResult('Ladder level does not exist.');
    }

    const hasCardsInLevel = currentState.cards.some(
      (card: Card) => card.showInLadder && card.priority === level,
    );

    if (hasCardsInLevel) {
      return this.createErrorResult('Only empty ladder levels can be deleted.');
    }

    const didCommit = this.mutateState((draft) => {
      const remainingLevels = draft.ladder.levels.filter((candidate) => candidate !== level).sort((a, b) => a - b);
      const levelMap = new Map(remainingLevels.map((originalLevel, index) => [originalLevel, index + 1] as const));

      draft.ladder.levels = remainingLevels.map((_, index) => index + 1);
      draft.cards = draft.cards.map((card) => {
        if (!card.showInLadder || card.priority === null) {
          return card;
        }

        const nextPriority = levelMap.get(card.priority);

        return nextPriority === undefined
          ? card
          : { ...card, priority: nextPriority, updatedAt: new Date().toISOString() };
      });
    });

    return didCommit ? { isValid: true, errors: [] } : this.createErrorResult('Failed to delete ladder level.');
  }

  public moveCardInLadder(
    cardId: string,
    targetLevel: number,
    targetIndex: number,
  ): CardOperationResult {
    const currentState = this.state();
    const card = currentState.cards.find((candidate: Card) => candidate.id === cardId);

    if (!card) {
      return this.createErrorResult('Card not found.');
    }

    if (!card.showInLadder) {
      return this.createErrorResult('Only cards already shown in the ladder can be moved.');
    }

    if (card.priority === null) {
      return this.createErrorResult('Card is missing ladder priority metadata.');
    }

    if (!currentState.ladder.levels.includes(targetLevel)) {
      return this.createErrorResult('Target ladder level does not exist.');
    }

    const visibleParent = card.parentCardId
      ? currentState.cards.find((candidate: Card) => candidate.id === card.parentCardId && candidate.showInLadder)
      : null;

    if (visibleParent) {
      return this.createErrorResult('Child cards cannot be moved independently while their parent is visible on the ladder.');
    }

    return this.commitCardMutation((draft) => {
      const timestamp = new Date().toISOString();
      const updatedCards = moveHierarchyEntry(
        draft.cards.filter((candidate: Card) => candidate.showInLadder),
        draft.ladder.levels,
        cardId,
        targetLevel,
        targetIndex,
      );
      const updatedCardsById = new Map(
        updatedCards.map((candidate) => [candidate.id, { ...candidate, updatedAt: timestamp }] as const),
      );

      draft.cards = draft.cards.map(
        (candidate) => updatedCardsById.get(candidate.id) ?? candidate,
      );
    }, 'Failed to move card in ladder.', cardId);
  }

  public moveChildInParentGroup(
    parentCardId: string,
    childCardId: string,
    targetIndex: number,
  ): CardOperationResult {
    const currentState = this.state();
    const childCard = currentState.cards.find((candidate: Card) => candidate.id === childCardId);
    const parentCard = currentState.cards.find((candidate: Card) => candidate.id === parentCardId);

    if (!childCard || !parentCard) {
      return this.createErrorResult('Card not found.');
    }

    if (childCard.parentCardId !== parentCardId) {
      return this.createErrorResult('Child card is not attached to the selected parent.');
    }

    if (!childCard.showInLadder || !parentCard.showInLadder) {
      return this.createErrorResult('Parent and child must both be visible on the ladder.');
    }

    return this.commitCardMutation((draft) => {
      const timestamp = new Date().toISOString();
      const updatedCards = reorderChildWithinHierarchy(
        draft.cards.filter((candidate: Card) => candidate.showInLadder),
        draft.ladder.levels,
        parentCardId,
        childCardId,
        targetIndex,
      );
      const updatedCardsById = new Map(
        updatedCards.map((candidate) => [candidate.id, { ...candidate, updatedAt: timestamp }] as const),
      );

      draft.cards = draft.cards.map(
        (candidate) => updatedCardsById.get(candidate.id) ?? candidate,
      );
    }, 'Failed to reorder child cards in ladder.', childCardId);
  }

  private commitCategoryMutation(
    recipe: (draft: AppState) => void,
    fallbackError: string,
  ): ValidationResult {
    const didCommit = this.mutateState(recipe);

    return didCommit ? { isValid: true, errors: [] } : this.createErrorResult(fallbackError);
  }

  private commitCardMutation(
    recipe: (draft: AppState) => void,
    fallbackError: string,
    cardId?: string,
  ): CardOperationResult {
    const didCommit = this.mutateState(recipe);

    return didCommit
      ? { isValid: true, errors: [], cardId }
      : this.createErrorResult(fallbackError);
  }

  private validateParentChange(
    childCard: Card,
    parentCardId: string | null,
    allCards: Card[],
  ): ValidationResult {
    if (!parentCardId) {
      return { isValid: true, errors: [] };
    }

    const targetParent = allCards.find((card: Card) => card.id === parentCardId);
    if (!targetParent) {
      return this.createErrorResult('Selected parent card no longer exists.');
    }

    const parentValidation = validateParentAssignment(childCard, targetParent, allCards);
    if (!parentValidation.isValid) {
      return parentValidation;
    }

    return { isValid: true, errors: [] };
  }

  private applyLadderRulesForNewCard(draft: AppState, card: Card): void {
    if (!card.showInLadder) {
      card.priority = null;
      card.importance = null;
      return;
    }

    if (draft.ladder.levels.length === 0) {
      draft.ladder.levels = [1];
    }

    const visibleParent = card.parentCardId
      ? draft.cards.find((candidate: Card) => candidate.id === card.parentCardId && candidate.showInLadder)
      : null;

    if (visibleParent?.priority !== null && visibleParent?.priority !== undefined) {
      card.priority = visibleParent.priority;

      const siblingCards = draft.cards
        .filter((candidate: Card) => candidate.showInLadder && candidate.parentCardId === visibleParent.id)
        .sort((left, right) => (left.importance ?? 0) - (right.importance ?? 0));

      const lastSiblingImportance = siblingCards.at(-1)?.importance ?? visibleParent.importance ?? 0;
      card.importance = lastSiblingImportance + 1;
      return;
    }

    const lastLevel = Math.max(...draft.ladder.levels);
    card.priority = card.priority ?? lastLevel;

    const laneCards = draft.cards.filter(
      (candidate: Card) => candidate.showInLadder && candidate.priority === card.priority,
    );
    card.importance = laneCards.length + 1;
  }

  private applyLadderRulesForExistingCard(
    draft: AppState,
    updatedCard: Card,
    wasInLadder: boolean,
    previousPriority: number | null,
    previousParentId: string | null,
  ): void {
    if (!updatedCard.showInLadder) {
      updatedCard.priority = null;
      updatedCard.importance = null;

      if (wasInLadder && previousPriority !== null) {
        const remainingLaneCards = draft.cards.filter(
          (card: Card) => card.showInLadder && card.priority === previousPriority,
        );
        const normalized = normalizeLane(remainingLaneCards);
        draft.cards = draft.cards.map(
          (card) => normalized.find((item) => item.id === card.id) ?? card,
        );
      }
      return;
    }

    if (draft.ladder.levels.length === 0) {
      draft.ladder.levels = [1];
    }

    const visibleParent = updatedCard.parentCardId
      ? draft.cards.find((card: Card) => card.id === updatedCard.parentCardId && card.showInLadder)
      : null;

    if (visibleParent?.priority !== null && visibleParent?.priority !== undefined) {
      updatedCard.priority = visibleParent.priority;

      const siblingCards = draft.cards
        .filter(
          (card: Card) =>
            card.showInLadder &&
            card.parentCardId === visibleParent.id &&
            card.id !== updatedCard.id,
        )
        .sort((left, right) => (left.importance ?? 0) - (right.importance ?? 0));
      const lastSiblingImportance = siblingCards.at(-1)?.importance ?? visibleParent.importance ?? 0;

      updatedCard.importance = lastSiblingImportance + 1;
    }

    if (previousParentId && !updatedCard.parentCardId && updatedCard.showInLadder) {
      const lastLevel = Math.max(...draft.ladder.levels);
      const bottomLaneCards = draft.cards.filter(
        (card: Card) =>
          card.showInLadder &&
          card.priority === lastLevel &&
          card.id !== updatedCard.id,
      );

      updatedCard.priority = lastLevel;
      updatedCard.importance = bottomLaneCards.length + 1;
    }

    if (updatedCard.priority === null || !draft.ladder.levels.includes(updatedCard.priority)) {
      updatedCard.priority = Math.max(...draft.ladder.levels);
    }

    if (updatedCard.importance === null || updatedCard.importance < 1) {
      const laneCards = draft.cards.filter(
        (card: Card) =>
          card.showInLadder && card.priority === updatedCard.priority && card.id !== updatedCard.id,
      );
      updatedCard.importance = laneCards.length + 1;
    }

    const laneCards = draft.cards.filter(
      (card: Card) => card.showInLadder && card.priority === updatedCard.priority,
    );
    const normalized = normalizeLane(laneCards);
    draft.cards = draft.cards.map((card) => normalized.find((item) => item.id === card.id) ?? card);
  }

  private normalizeLadderHierarchyInState(draft: AppState): boolean {
    const ladderCards = draft.cards.filter((card: Card) => card.showInLadder);
    const nextLevels = createSequentialLadderLevels(ladderCards, draft.ladder.levels);
    const normalizedCards = normalizeHierarchicalLadder(ladderCards, nextLevels);
    const normalizedCardsById = new Map(normalizedCards.map((card) => [card.id, card] as const));
    const previousSnapshot = JSON.stringify({
      levels: draft.ladder.levels,
      cards: ladderCards.map((card) => ({
        id: card.id,
        priority: card.priority,
        importance: card.importance,
      })),
    });

    draft.ladder.levels = nextLevels;
    draft.cards = draft.cards.map((card) => normalizedCardsById.get(card.id) ?? card);

    const nextSnapshot = JSON.stringify({
      levels: draft.ladder.levels,
      cards: draft.cards
        .filter((card) => card.showInLadder)
        .map((card) => ({
          id: card.id,
          priority: card.priority,
          importance: card.importance,
        })),
    });

    return previousSnapshot !== nextSnapshot;
  }

  private mergeCardDraftIntoCard(baseCard: Card, draft: CardDraft, updatedAt: string): Card {
    return {
      ...baseCard,
      title: draft.title.trim(),
      description: draft.description.trim(),
      notes: draft.notes.trim(),
      categoryId: draft.categoryId,
      status: draft.status,
      showInLadder: draft.showInLadder,
      parentCardId: draft.parentCardId,
      deadline: draft.deadline,
      manualProgress: draft.manualProgress,
      ladderGroupColor: draft.ladderGroupColor ?? baseCard.ladderGroupColor ?? null,
      updatedAt,
    };
  }

  private applyAndPersistState(nextState: AppState): boolean {
    const validationResult = validateAppState(nextState);

    if (!validationResult.isValid) {
      console.error('Mutation aborted due to validation failures:', validationResult.errors);
      return false;
    }

    this.normalizeLadderHierarchyInState(nextState);

    const normalizedValidationResult = validateAppState(nextState);

    if (!normalizedValidationResult.isValid) {
      console.error('Mutation aborted due to post-normalization validation failures:', normalizedValidationResult.errors);
      return false;
    }

    this.state.set(nextState);
    this.storage.saveState(nextState);

    return true;
  }

  private createErrorResult(message: string): ValidationResult {
    return {
      isValid: false,
      errors: [message],
    };
  }
}
